import { DEFAULT_MESSAGE_TYPE_COLORS, type MessageTypeWithStats } from '@owl/shared';
import { useCallback, useState } from 'react';
import { useConfirm } from '../components/Confirm';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { Icon } from '../components/icons';
import { activateOnKey, isFromRowActions } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useLoader } from '../lib/useLoader';

type ViewMode = 'grid' | 'list';

const STORAGE_KEY = 'owl.mt.view';

function loadViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'grid';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'list' ? 'list' : 'grid';
}

export function MessageTypes({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const [items, setItems] = useState<MessageTypeWithStats[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MessageTypeWithStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(loadViewMode);
  const { push } = useToast();
  const confirm = useConfirm();

  function setViewMode(v: ViewMode) {
    setView(v);
    try {
      window.localStorage.setItem(STORAGE_KEY, v);
    } catch {
      // ignore
    }
  }

  const load = useCallback(async () => {
    try {
      const { items } = await api.listMessageTypes();
      setItems(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useLoader(load, refreshTick);

  async function handleDelete(mt: MessageTypeWithStats) {
    const ok = await confirm({
      title: 'Delete message type',
      sub: mt.name,
      body:
        mt.logs24h > 0
          ? `${mt.name} has ${mt.logs24h} logs in the last 24h. Force-delete cascades those logs.`
          : 'The message type is removed from the catalogue.',
      confirmLabel: mt.logs24h > 0 ? 'Force-delete' : 'Delete type',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteMessageType(mt.id, mt.logs24h > 0);
      push(`${mt.name} deleted`);
      load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Delete failed');
    }
  }

  async function handleResetColor(mt: MessageTypeWithStats) {
    const def = DEFAULT_MESSAGE_TYPE_COLORS[mt.name];
    if (!def || def.toLowerCase() === mt.color.toLowerCase()) return;
    try {
      await api.updateMessageType(mt.id, { color: def });
      push(`${mt.name} color restablecido`);
      load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Reset failed');
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <span className="serif-i">Message types</span>
          </h1>
          <div className="page-sub">
            Categorías de log con su color asignado. Afectan a la barra lateral del feed, al chip y
            al donut de distribución.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="seg" title="Vista">
            <button
              type="button"
              className={`seg-opt ${view === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Cuadrícula"
            >
              <Icon.Apps size={12} />
            </button>
            <button
              type="button"
              className={`seg-opt ${view === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Lista"
            >
              <Icon.Types size={12} />
            </button>
          </div>
          <button type="button" className="btn btn-accent" onClick={() => setCreating(true)}>
            <Icon.Plus size={13} /> New message type
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : view === 'grid' ? (
        <div className="mt-grid">
          {items.map((mt) => {
            const defaultColor = DEFAULT_MESSAGE_TYPE_COLORS[mt.name];
            const canReset =
              defaultColor !== undefined && defaultColor.toLowerCase() !== mt.color.toLowerCase();
            return (
              <div className="mt-card" key={mt.id}>
                <div className="mt-bar" style={{ background: mt.color }} />
                <div className="mt-card-body">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="mt-name" style={{ color: mt.color }}>
                      {mt.name}
                    </span>
                    <span
                      className="chip chip-mt"
                      style={{
                        background: `${mt.color}1F`,
                        borderColor: `${mt.color}55`,
                        color: mt.color,
                      }}
                    >
                      {mt.name}
                    </span>
                  </div>
                  <div className="mt-desc">{mt.description ?? '—'}</div>
                  <div className="mt-stats">
                    <div>
                      <div className="label-xs">Logs · 24h</div>
                      <div className="mono" style={{ fontSize: 18, marginTop: 4 }}>
                        {mt.logs24h.toLocaleString()}
                      </div>
                    </div>
                    <div className="mt-actions">
                      {canReset && (
                        <button
                          type="button"
                          className="btn-icon btn-ghost"
                          onClick={() => handleResetColor(mt)}
                          title={`Restablecer color por defecto (${defaultColor})`}
                        >
                          <Icon.Refresh size={13} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-icon btn-ghost"
                        onClick={() => setEditing(mt)}
                        title="Edit"
                      >
                        <Icon.Edit size={13} />
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-ghost"
                        onClick={() => handleDelete(mt)}
                        title="Delete"
                      >
                        <Icon.Trash size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Color</th>
                <th>Logs 24h</th>
                <th>Description</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((mt) => {
                const defaultColor = DEFAULT_MESSAGE_TYPE_COLORS[mt.name];
                const canReset =
                  defaultColor !== undefined &&
                  defaultColor.toLowerCase() !== mt.color.toLowerCase();
                return (
                  <tr
                    key={mt.id}
                    className="data-row"
                    tabIndex={0}
                    onClick={(e) => {
                      if (isFromRowActions(e)) return;
                      setEditing(mt);
                    }}
                    onKeyDown={(e) => activateOnKey(e, () => setEditing(mt))}
                  >
                    <td>
                      <span
                        className="chip chip-mt"
                        style={{
                          background: `${mt.color}1F`,
                          borderColor: `${mt.color}55`,
                          color: mt.color,
                        }}
                      >
                        {mt.name}
                      </span>
                    </td>
                    <td className="mono dim">{mt.color}</td>
                    <td className="mono">{mt.logs24h}</td>
                    <td className="dim">{mt.description}</td>
                    <td>
                      <div className="row-actions">
                        {canReset && (
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => handleResetColor(mt)}
                            title={`Restablecer color por defecto (${defaultColor})`}
                          >
                            <Icon.Refresh size={12} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => handleDelete(mt)}
                          title="Delete"
                        >
                          <Icon.Trash size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <MessageTypeModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      )}
      {editing && (
        <MessageTypeModal
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function MessageTypeModal({
  existing,
  onClose,
  onSaved,
}: {
  existing?: MessageTypeWithStats;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeKey(onClose);
  const [name, setName] = useState(existing?.name ?? '');
  const [color, setColor] = useState(existing?.color ?? '#3B82F6');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (existing) {
        await api.updateMessageType(existing.id, { color, description: description || undefined });
      } else {
        await api.createMessageType({
          name: name.toUpperCase(),
          color,
          description: description || undefined,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal">
        <div className="modal-head">
          <div className="modal-title-block">
            <div className="label-sm" style={{ marginBottom: 6 }}>
              {existing ? 'Editar' : 'Nuevo'}
            </div>
            <h2 className="modal-title">
              Message <span className="serif-i">type</span>
            </h2>
          </div>
          <button type="button" className="btn-icon btn-ghost" onClick={onClose}>
            <Icon.X size={15} />
          </button>
        </div>
        <div className="modal-body">
          <div className="field">
            <div className="field-label">Name</div>
            <input
              className="input mono"
              style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
              value={name}
              disabled={!!existing}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              placeholder="WARN"
              required
            />
            <div className="field-hint">
              Uppercase letters/digits/underscore. Cannot be changed later.
            </div>
          </div>
          <div className="field">
            <div className="field-label">Color</div>
            <div className="custom-swatch">
              <span className="custom-swatch-sq" style={{ background: color }} />
              <input type="text" value={color} onChange={(e) => setColor(e.target.value)} />
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  width: 24,
                  height: 24,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
            </div>
          </div>
          <div className="field">
            <div className="field-label">Description</div>
            <textarea
              className="textarea"
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Cuándo usar este tipo de log..."
            />
          </div>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <div className="modal-foot-info">
            Los cambios se aplican a todos los logs del histórico.
          </div>
          <div className="modal-foot-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => submit()}
              disabled={busy}
            >
              {busy ? 'Guardando…' : existing ? 'Guardar cambios' : 'Crear tipo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
